import { useCallback, useEffect, useRef, useState } from "react";

import { loadLocationForecasts } from "../data/weatherRepository";
import {
  buildGameForecast,
  calendarDayDistance,
  coordinateKey,
  coordinatesForField,
  dateInToronto,
  gameWindowMinutes,
  WEATHER_FORECAST_DAYS,
} from "../domain/weather";
import type {
  ScheduleForecastState,
  WeatherCoordinates,
} from "../domain/weather";

interface WeatherScheduleEntry {
  id: string;
  status: "scheduled" | "active" | "completed";
  scheduledDate: string;
  timeSlot: string | null;
  field: string | null;
}

interface UseScheduleWeatherOptions {
  enabled: boolean;
  entries: WeatherScheduleEntry[];
}

export function useScheduleWeather({
  enabled,
  entries,
}: UseScheduleWeatherOptions): {
  forecastsByEntryId: Record<string, ScheduleForecastState>;
  retry: () => void;
  refreshing: boolean;
} {
  const [refreshToken, setRefreshToken] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const [forecastsByEntryId, setForecastsByEntryId] = useState<
    Record<string, ScheduleForecastState>
  >({});
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const entriesKey = entries
    .map((entry) =>
      [
        entry.id,
        entry.status,
        entry.scheduledDate,
        entry.timeSlot,
        entry.field,
      ].join(":"),
    )
    .join("|");
  const retry = useCallback(() => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    setRefreshToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setForecastsByEntryId({});
      refreshingRef.current = false;
      setRefreshing(false);
      return undefined;
    }

    const controller = new AbortController();
    const today = dateInToronto();
    const upcoming = entriesRef.current.filter(
      (entry) => entry.status !== "completed",
    );
    const initial = initialStates(upcoming, today);
    const eligible = upcoming.filter(
      (entry) => initial[entry.id]?.status === "loading",
    );
    setForecastsByEntryId((current) =>
      Object.fromEntries(
        Object.entries(initial).map(([id, state]) => [
          id,
          state.status === "loading" && current[id]?.status === "available"
            ? current[id]
            : state,
        ]),
      ),
    );
    if (!eligible.length) {
      refreshingRef.current = false;
      setRefreshing(false);
      return () => controller.abort();
    }
    refreshingRef.current = true;
    setRefreshing(true);

    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const coordinates = eligible
          .map((entry) => coordinatesForField(entry.field))
          .filter(
            (location): location is WeatherCoordinates => location !== null,
          );
        const result = await loadLocationForecasts(coordinates, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const next = initialStates(upcoming, today);
        for (const entry of eligible) {
          const location = coordinatesForField(entry.field);
          const locationForecast = location
            ? result.byCoordinate.get(coordinateKey(location))
            : null;
          const forecast = locationForecast
            ? buildGameForecast(
                entry.scheduledDate,
                entry.timeSlot,
                locationForecast.hours,
                locationForecast.dayPrecipitationByDate.get(
                  entry.scheduledDate,
                ) ?? null,
              )
            : null;
          next[entry.id] = forecast
            ? { status: "available", forecast }
            : { status: "unavailable" };
        }
        setForecastsByEntryId(next);
        refreshTimer = setTimeout(
          () => setRefreshToken((token) => token + 1),
          Math.max(1000, result.expiresAt - Date.now()),
        );
      } catch (error) {
        if (controller.signal.aborted || errorName(error) === "AbortError")
          return;
        setForecastsByEntryId((current) => {
          const next = initialStates(upcoming, today);
          for (const entry of eligible) {
            const previous = current[entry.id];
            next[entry.id] =
              previous?.status === "available" ? previous : { status: "error" };
          }
          return next;
        });
      } finally {
        if (!controller.signal.aborted) {
          refreshingRef.current = false;
          setRefreshing(false);
        }
      }
    };
    void load();

    return () => {
      controller.abort();
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [enabled, entriesKey, refreshToken]);

  return { forecastsByEntryId, retry, refreshing };
}

function initialStates(
  entries: WeatherScheduleEntry[],
  today: string,
): Record<string, ScheduleForecastState> {
  return Object.fromEntries(
    entries.map((entry) => {
      const distance = calendarDayDistance(today, entry.scheduledDate);
      if (distance === null || distance < 0) {
        return [entry.id, { status: "unavailable" }];
      }
      if (distance >= WEATHER_FORECAST_DAYS) {
        return [entry.id, { status: "out_of_range" }];
      }
      if (
        !coordinatesForField(entry.field) ||
        !gameWindowMinutes(entry.timeSlot)
      ) {
        return [entry.id, { status: "unavailable" }];
      }
      return [entry.id, { status: "loading" }];
    }),
  );
}

function errorName(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("name" in error)) {
    return null;
  }
  return typeof error.name === "string" ? error.name : null;
}
